// Space mutations. Each hook calls the API directly via `getApiClient()` and
// patches the QueryClient cache.
//
// After the 0015 cutover, post/comment/reaction mutations have been folded
// into `lib/mutations/feed.ts` — spaces use the unified posts surface with
// scope={ kind: 'space', id: spaceId }.

import {
  useMutation,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';
import { trackGroupCreated } from '../analytics/events';
import { getApiClient } from '../api-client';
import { unwrap } from '../api-error';
import {
  spacesKeys,
  type SpaceDetail,
  type SpaceSummaryWithViewerRole,
} from '../queries/spaces';
import { type SnapshotSet } from '../query-cache-utils';
import { transformAllListCaches } from '../cache/transform-all-list-caches';
import { invalidateGroupAfterMutation } from '../cache/group-invalidation';

// ---------- Space membership optimistic-cache helpers ----------
//
// Spaces store viewer state on the row itself: SpaceSummaryWithViewerRole
// carries `viewerRole`; SpaceDetail carries `viewerRole` + `viewerHasJoined`.
// Both reads — list (infinite + flat) and detail — need to flip together so
// the join CTA / leave button derives from cache instantly. Mirrors the
// groups helpers; structurally different because spaces don't surface a
// pending-request projection on the list shape yet.

type SpacesPage = { readonly items: readonly SpaceSummaryWithViewerRole[] };
type SpacesCache = SpacesPage | InfiniteData<SpacesPage>;

function patchAllSpaceListsInCache(
  qc: QueryClient,
  spaceId: string,
  role: 'leader' | 'member' | null,
): SnapshotSet {
  const patchItems = (
    items: readonly SpaceSummaryWithViewerRole[],
  ): readonly SpaceSummaryWithViewerRole[] =>
    items.map((s) => (s.id === spaceId ? { ...s, viewerRole: role } : s));
  return transformAllListCaches<SpacesCache>(
    qc,
    spacesKeys.lists(),
    (value) => {
      if ('pages' in value) {
        return {
          ...value,
          pages: value.pages.map((p) => ({ ...p, items: patchItems(p.items) })),
        };
      }
      return { ...value, items: patchItems(value.items) };
    },
  );
}

function patchSpaceDetail(
  qc: QueryClient,
  spaceId: string,
  role: 'leader' | 'member' | null,
): SnapshotSet {
  const key = spacesKeys.detail(spaceId).queryKey;
  const prev = qc.getQueryData<SpaceDetail>(key);
  if (prev === undefined) return { previousLists: [] };
  qc.setQueryData<SpaceDetail>(key, {
    ...prev,
    viewerRole: role,
    viewerHasJoined: role !== null,
    // Optimistic mirror of the server's `postableBy`: joining grants the
    // member floor, leaving falls back to the open floor only. The refetch
    // on settle re-asserts the server's answer.
    viewerCanPost:
      role !== null
        ? prev.minPostRole !== 'leader' || role === 'leader'
        : prev.minPostRole === 'anyone',
  });
  return { previousLists: [[key, prev]] };
}

export function applySpaceJoinPatch(
  qc: QueryClient,
  spaceId: string,
  role: 'leader' | 'member' | null,
): SnapshotSet {
  const list = patchAllSpaceListsInCache(qc, spaceId, role);
  const detail = patchSpaceDetail(qc, spaceId, role);
  return { previousLists: [...list.previousLists, ...detail.previousLists] };
}

export type CreateSpaceInput = {
  name: string;
  description?: string;
  tone?: string;
  access: 'public' | 'unlisted' | 'private';
  joinPolicy?: 'open' | 'request' | 'invite_only';
};

export function useCreateSpace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateSpaceInput): Promise<SpaceDetail> => {
      const client = getApiClient();
      const data = unwrap(await client.POST('/api/v1/spaces', {
        body: {
          name: input.name,
          access: input.access,
          ...(input.description !== undefined
            ? { description: input.description }
            : {}),
          ...(input.tone !== undefined ? { tone: input.tone } : {}),
          ...(input.joinPolicy !== undefined
            ? { joinPolicy: input.joinPolicy }
            : {}),
        },
      }));
      return data.data;
    },
    onSuccess: (_data, input) => {
      // groups.kind 'space' is member-facing "group" — see groupKindToType.
      trackGroupCreated({
        group_type: 'group',
        access: input.access,
        ...(input.joinPolicy !== undefined
          ? { join_policy: input.joinPolicy }
          : {}),
      });
      void qc.invalidateQueries({ queryKey: spacesKeys.lists() });
    },
  });
}

export type UpdateSpaceInput = {
  id: string;
  name?: string;
  description?: string;
  tone?: string;
  memberRules?: string | null;
  access?: 'public' | 'unlisted' | 'private';
  minPostRole?: 'anyone' | 'member' | 'leader';
  // joinPolicy is derived from access server-side.
};

export function useUpdateSpace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateSpaceInput): Promise<SpaceDetail> => {
      const client = getApiClient();
      const body: Record<string, unknown> = {};
      if (input.name !== undefined) body['name'] = input.name;
      if (input.description !== undefined)
        body['description'] = input.description;
      if (input.tone !== undefined) body['tone'] = input.tone;
      if (input.memberRules !== undefined) body['memberRules'] = input.memberRules;
      if (input.access !== undefined) body['access'] = input.access;
      if (input.minPostRole !== undefined) body['minPostRole'] = input.minPostRole;
      const data = unwrap(await client.PUT('/api/v1/spaces/{id}', {
        params: { path: { id: input.id } },
        body: body as {
          name?: string;
          description?: string;
          tone?: string;
          memberRules?: string | null;
          access?: 'public' | 'unlisted' | 'private';
          joinPolicy?: 'open' | 'request' | 'invite_only';
          minPostRole?: 'anyone' | 'member' | 'leader';
        },
      }));
      return data.data;
    },
    onSuccess: (data, vars) => {
      qc.setQueryData(spacesKeys.detail(vars.id).queryKey, data);
      invalidateGroupAfterMutation(qc, 'space', vars.id, 'update');
    },
  });
}

export function useInviteToSpace() {
  return useMutation({
    mutationFn: async (input: {
      id: string;
      emails: readonly string[];
      roleOnAccept?: 'member' | 'leader';
    }) => {
      const client = getApiClient();
      const data = unwrap(await client.POST(
        '/api/v1/spaces/{id}/invites',
        {
          params: { path: { id: input.id } },
          body: {
            invitedEmails: [...input.emails],
            roleOnAccept: input.roleOnAccept ?? 'member',
          },
        },
      ));
      return data.items;
    },
  });
}

